import { Inject, Injectable, Optional } from '@angular/core';
import { FingerprintAIO } from '@ionic-native/fingerprint-aio/ngx';
import { AndroidFingerprintAuth } from '@ionic-native/android-fingerprint-auth/ngx';
import { TouchID } from '@ionic-native/touch-id/ngx';
import { Keychain } from '@ionic-native/keychain/ngx';
import { Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, from } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { StorageService } from './storage.service';
import { Config } from '../tokens';
import { FingerprintAuthConfiguration } from '../interfaces';

export enum Biometric {
  Face = 'face', // Will use Face recognition
  Fingerprint = 'finger', // Will use fingerprint
  Common = 'biometric', // Device has some kind of biometric encryption
}

export interface BiometricsAvailable {
  face: boolean;
  touch: boolean;
}

export type Biometrics = 'face' | 'fingerprint' | 'biometric';

@Injectable()
export class FingerprintService {
  constructor(
    private platform: Platform,
    private faio: FingerprintAIO,
    private androidFingerprintAuth: AndroidFingerprintAuth,
    private touchId: TouchID,
    private keychain: Keychain,
    private router: Router,
    private storage: StorageService,
    @Optional() private translateService: TranslateService,
    @Inject(Config) private readonly config: FingerprintAuthConfiguration,
  ) {}

  /**
   * Use this method in Login page to check if user
   * should be redirected to the Biometric Activator
   */
  public async checkIfNeedsBiometric(user: string) {
    if (!this.isPlatformMobile()) {
      this.config.debug &&
        console.log(
          '[BiometricService]',
          'isPlatformMobile():',
          this.isPlatformMobile(),
        );
      return false;
    }
    // Check FaceID enabled
    const biometricFaceActive = await this.isBiometricActive(user, 'face');
    this.config.debug &&
      console.log(
        '[BiometricService]',
        'biometricFaceActive:',
        biometricFaceActive,
      );
    // Check TouchID enabled
    const biometricTouchActive = await this.isBiometricActive(user, 'touch');
    this.config.debug &&
      console.log(
        '[BiometricService]',
        'biometricTouchActive:',
        biometricTouchActive,
      );
    // Check if device has some biometric capability
    const biometricCapabilities = await this.retrieveDeviceBiometrics();
    this.config.debug &&
      console.log(
        '[BiometricService]',
        'biometricCapabilities:',
        biometricCapabilities,
      );
    const isBiometricCapable =
      biometricCapabilities.face || biometricCapabilities.touch;
    // Redirect to Biometric Activator if conditions are met
    return (
      biometricFaceActive === null &&
      biometricTouchActive === null &&
      isBiometricCapable
    );
  }

  /**
   * Use in Login page to open Biometric prompt
   * @param user string
   */
  public async showBiometricPrompt(user: string) {
    const face = await this.showFingerprintId(user, 'face').toPromise();
    const touch = await this.showFingerprintId(user, 'touch').toPromise();
    // Here we have a big dilemma
    // In case the user device is capable of FaceID and TouchID,
    // for the moment we will mark `face` as preferent,
    // as iOS the mostly one with FaceID
    if (face) {
      // Login with FaceID
      const password = await this.launchFaceID(user);
      if (password) {
        return { user, password };
      } else {
        throw 'Something went wrong in launchFaceID';
      }
    } else if (touch) {
      // Login with TouchID
      const password = await this.launchTouchID(user);
      if (password) {
        return { user, password };
      } else {
        throw 'Something went wrong in launchTouchID';
      }
    } else {
      throw 'Looks like we have an error in our code.';
    }
  }

  private async _iosShowTouchPrompt() {
    // As found out, iOS devices with touch capability
    // needs to use TouchID plugin with text fallback function
    // otherwise the device will save the fingerprint without prompting the user
    try {
      return await this.touchId.verifyFingerprintWithCustomPasswordFallback(
        'Scan your fingerprint please',
      );
    } catch (err) {
      // -3 code means the user used the Use Password fallback, which is ok for us
      if (err?.code !== -3) {
        throw err;
      }
      return true;
    }
  }

  private _sanitizeUser(user: string) {
    return `${user}`.toLowerCase();
  }

  private _getStorageKey(
    user: string,
    biometricType: keyof BiometricsAvailable,
  ) {
    user = this._sanitizeUser(user);
    return `biometricLoginActive_${biometricType}_${user}`;
  }

  private _getStorageToken(
    user: string,
    biometricType: keyof BiometricsAvailable,
  ) {
    user = this._sanitizeUser(user);
    return `token_${biometricType}_${user}`;
  }

  private getLang() {
    return this.translateService?.currentLang || 'es';
  }

  public async isBiometricActive(
    user: string,
    biometricType: keyof BiometricsAvailable,
  ) {
    return await this.storage.get(this._getStorageKey(user, biometricType));
  }

  public isPlatformMobile() {
    return (
      this.platform.is('ios') ||
      this.platform.is('ipad') ||
      this.platform.is('iphone') ||
      this.platform.is('android')
    );
  }

  public async clearBiometricData(
    user: string,
    biometricType: keyof BiometricsAvailable,
  ) {
    await this.storage.remove(this._getStorageKey(user, biometricType));
    await this.storage.remove(this._getStorageToken(user, biometricType));
    if (this._isIOS()) {
      await this.keychain.remove(this._getStorageToken(user, biometricType));
    }
  }

  public async disableBiometricData(
    user: string,
    biometricType: keyof BiometricsAvailable,
  ) {
    await this.storage.set(this._getStorageKey(user, biometricType), false);
    await this.storage.remove(this._getStorageToken(user, biometricType));
    if (this._isIOS()) {
      await this.keychain.remove(this._getStorageToken(user, biometricType));
    }
  }

  public showFingerprintId(
    user: string,
    biometricType: keyof BiometricsAvailable,
  ) {
    this.config.debug &&
      console.log(
        '[BiometricService]',
        'showFingerPrintId with type:',
        biometricType,
      );
    this.config.debug &&
      console.log('[BiometricService]', 'Is IOS:', this._isIOS());
    // Retrieve the getter for iOS or Android
    const tokenPromise = this._isIOS()
      ? this.keychain.get(this._getStorageToken(user, biometricType))
      : this.storage.get(this._getStorageToken(user, biometricType));
    this.config.debug &&
      console.log('[BiometricService]', 'Token Promise:', tokenPromise);
    // User user to retrieve status of biometric and check if it has token
    return forkJoin([
      from(this.storage.get(this._getStorageKey(user, biometricType))),
      from(tokenPromise),
    ]).pipe(
      tap(
        ([active, token]) =>
          this.config.debug &&
          console.log(
            '[BiometricService]',
            'Biometric result |',
            'Active:',
            active,
            'Token:',
            token,
          ),
      ),
      map(([active, token]) => !!active && !!token),
    );
  }

  /**
   * Use in Login page to show the Biometric Activator page
   * @param user User
   * @param password Password
   * @param callbackUrl URL to return after success or fail
   */
  public showBiometricActivator(
    user: string,
    password: string,
    callbackUrl: string,
  ) {
    this.router.navigate(['biometric-activator'], {
      queryParams: { user, password, callbackUrl },
    });
  }

  /**
   * Checks and returns which biometrics methods are available
   * for the current device
   * @returns Promise<Biometric[]>
   */
  public async retrieveDeviceBiometrics(): Promise<BiometricsAvailable> {
    await this.platform.ready();
    let iosResult = '';
    let androidResult = null;
    if (this._isIOS()) {
      try {
        iosResult = await this.touchId.isAvailable();
      } catch (err) {}
    } else {
      try {
        androidResult = await this.androidFingerprintAuth.isAvailable();
      } catch (err) {}
    }
    return {
      face: iosResult === 'face',
      touch: iosResult === 'touch' || androidResult?.isAvailable || false,
    };
  }

  private _isIOS() {
    return (
      this.platform.is('ios') ||
      this.platform.is('ipad') ||
      this.platform.is('iphone')
    );
  }

  /**
   * Stores the credentials used for biometrics
   * @param user Client user
   * @param password Client password
   */
  private async _storePassword(
    user: string,
    password: string,
    biometricType: keyof BiometricsAvailable,
  ) {
    // Save user for later use in Login
    if (this._isIOS()) {
      // IOS - Use Keychain
      const biometric = await this.touchId.isAvailable();
      // Set Biometric login in storage
      await this.storage.set(this._getStorageKey(user, biometricType), true);
      // We check for `touch` or `face`, although is irrelevant
      // as the encryption prompt as already been shown
      if (biometric == 'touch' || biometric == 'face') {
        if (biometric == 'touch') {
          await this._iosShowTouchPrompt();
        }
        // Although we define the prefix "token_", it doesn't actually contain a token,
        // it will contain the password in the encrypted iOS storage
        await this.keychain.set(
          this._getStorageToken(user, biometricType),
          password,
          false,
        );
      }
    } else if (this.platform.is('android')) {
      // Android - Use AndroidFingerprintAuth
      const fingerprint = await this.androidFingerprintAuth.isAvailable();
      if (fingerprint.isAvailable) {
        // Fingerprint is available, encrypt!
        let fingerprintResult = await this.androidFingerprintAuth.encrypt({
          clientId: user,
          username: user,
          password: password,
          locale: this.getLang(),
          disableBackup: true,
        });
        if (fingerprintResult.withFingerprint) {
          // User used fingerprint to decrypt password
          this.storage.set(this._getStorageKey(user, biometricType), true);
          this.storage.set(
            this._getStorageToken(user, biometricType),
            fingerprintResult.token,
          );
          this.config.debug &&
            console.log(`Token (${user}): ${fingerprintResult.token}`);
        } else if (fingerprintResult.withBackup) {
          // DEPRECATED: Disabled "Use backup" option from encrypt prompt as it is useless
          // console.log('Successfully authenticated with backup password!');
        } else {
          this.config.debug && console.log("Didn't authenticate!");
        }
      }
    }
  }

  /**
   * Shows the FaceID dialog to the user
   * @param user Client user
   * @returns Promise<void>
   */
  public async activateFaceID(user: string, password: string) {
    const biometricConfig = {
      cancelButtonTitle: 'Cancelar',
      description: 'Activar FaceID',
      disableBackup: true,
      title: 'Activate',
      fallbackButtonTitle: 'Back Button',
      subtitle: 'Act',
    };
    try {
      // Show FaceID dialog to user
      await this.faio.show(biometricConfig);
    } catch (err) {
      console.log('Match not found');
      console.log(err);
    }
    // Save encrypted password
    await this._storePassword(user, password, 'face');
  }

  /**
   * Shows the TouchID dialog to the user
   * @param user Client user
   * @param password Client password
   */
  public activateTouchID(user: string, password: string) {
    return this._storePassword(user, password, 'touch');
  }

  /**
   * Executes an attempt to check for the client fingerprint
   * Used mainly in Login
   * @returns Promise<void>
   */
  public async launchTouchID(user: string): Promise<string | null> {
    if (this._isIOS()) {
      await this.touchId.isAvailable(); // Will reject if not available
      await this._iosShowTouchPrompt(); // Will reject if fingerprint was failed
      const password = <string>(
        await this.keychain.get(this._getStorageToken(user, 'touch'))
      );
      if (password) {
        return password;
      } else {
        console.log('Password not found in keychain');
      }
    } else {
      const fingerprintAvailable = (
        await this.androidFingerprintAuth.isAvailable()
      ).isAvailable;
      if (fingerprintAvailable) {
        const token = <string>(
          await this.storage.get(this._getStorageToken(user, 'touch'))
        );
        if (token) {
          const fingerprintResult = await this.androidFingerprintAuth.decrypt({
            clientId: user,
            username: user,
            token: token,
            locale: this.getLang(),
          });
          if (fingerprintResult.password) {
            return fingerprintResult.password;
          } else {
            console.log('Password not found in decrypted fingerprint');
          }
        } else {
          console.log('Token not found in storage');
        }
      }
    }
  }

  /**
   * Executes an attempt to check for the client face
   * Used mainly in Login
   * @returns Promise<void>
   */
  public async launchFaceID(user: string): Promise<string | null> {
    const faceAvailable = await this.faio.isAvailable();
    if (faceAvailable) {
      try {
        await this.faio.show({
          cancelButtonTitle: 'Cancel',
          description: 'Acceder con FaceId',
          disableBackup: true,
          title: 'Scan',
          fallbackButtonTitle: 'FB back Button',
          subtitle: 'Subtitle',
        });
      } catch (err) {
        console.log('No match found');
        console.log(err);
        return null;
      }
      const password = <string>(
        await this.keychain.get(this._getStorageToken(user, 'face'))
      );
      if (password) {
        return password;
      } else {
        console.log('Password not found in keychain');
      }
    } else {
      console.log('FaceID is not available');
    }
  }
}
